import React, { useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { Navigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { downloadAuditLogs, clearAuditLogs } from '../services/auditLogs';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../contexts/langContext';

const AuditLogsPage: React.FC = () => {
  const { lang } = useLang();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [clearing, setClearing] = useState(false);

  if (user && user.role !== 'Admin') {
    return <Navigate to="/projects" replace />;
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadAuditLogs();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'audit.log';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download audit logs', error);
      enqueueSnackbar('Failed to download audit logs', { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await clearAuditLogs();
    } catch (error) {
      console.error('Failed to clear audit logs', error);
      enqueueSnackbar(lang === 'en' ? 'Failed to clear audit logs' : '清除审计日志失败', { variant: 'error' });
    } finally {
      setClearing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 4, maxWidth: 720, margin: '0 auto' }} elevation={2}>
        <Stack spacing={3}>
          <Typography variant="h5">
            {lang === 'en' ? 'Audit Logs' : '审计'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {lang === 'en' ? "Audit logs capture key administrative and user actions in JSON Lines format. Download the current log for review or clear it after archiving through your organization's retention workflow." : '审计日志捕获关键的管理和用户操作，以 JSON Lines 格式存储。下载当前日志以供审查，或通过您的组织的保留工作流归档后清除。'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              startIcon={<CloudDownloadIcon />}
              onClick={handleDownload}
              disabled={downloading || clearing}
            >
              {downloading ? 'Downloading…' : lang === 'en' ? 'Download Logs' : '下载日志'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleClear}
              disabled={clearing || downloading}
            >
              {clearing ? 'Clearing…' : lang === 'en' ? 'Clear Logs' : '清除日志'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {lang === 'en' ? 'Logs are stored as JSONL at the configured audit log path. Integrate with your log rotation tooling to archive regularly and avoid disk saturation.' : '日志存储为JSONL格式，位于配置的审计日志路径。与您的日志轮转工具集成，定期归档以避免磁盘饱和。'}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default AuditLogsPage;
